/**
 * The gopher, used where it says something.
 *
 * Five poses came from the design sheet and each is placed against the section
 * it actually illustrates — the detective beside investigation, the runner
 * beside the pipeline gate, the one at a laptop beside the setup. A mascot
 * scattered for decoration is noise; one that matches the paragraph beside it
 * is a second way of reading the page.
 *
 * They live in public/ rather than in the bundle, and they are WebP: the five
 * of them together are 73 kB, where the same crops as PNG were 396 kB. On a
 * page arguing that performance is noticed, the illustrations are not the place
 * to stop caring.
 *
 * They sit on light and dark sections alike. The crops carry real transparency
 * and the anti-aliased edge is drawn in the figure's own colour rather than in
 * white, so there is no fringe to hide on either ground — checked pixel by
 * pixel rather than assumed, because a halo is the sort of thing that looks
 * fine on the monitor it was made on.
 */

export type Pose = 'pointer' | 'laptop' | 'hero' | 'detective' | 'running'

export function Mascot({
  pose,
  size = 150,
  hideOnMobile = true,
  flip = false,
  alt,
}: {
  pose: Pose
  size?: number
  hideOnMobile?: boolean
  /**
   * Mirror horizontally. The poses that hold something — the pointer's stick,
   * the magnifying glass — face right as drawn, and an illustration facing away
   * from the thing it is pointing at leads the eye off the edge of the section.
   */
  flip?: boolean
  /**
   * Empty by default. These carry no information a screen reader needs — the
   * text beside them says the same thing — and describing a cartoon rodent to
   * somebody who cannot see it is noise rather than access.
   */
  alt?: string
}) {
  return (
    <img
      src={`/mascots/${pose}.webp`}
      alt={alt ?? ''}
      aria-hidden={alt ? undefined : true}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      className={hideOnMobile ? 'mascot mascot--desktop' : 'mascot'}
      style={{
        width: size,
        height: 'auto',
        objectFit: 'contain',
        flexShrink: 0,
        transform: flip ? 'scaleX(-1)' : undefined,
      }}
    />
  )
}
